import * as React from 'react'
import {
    Alert,
    Box,
    Button,
    Checkbox,
    Chip,
    Divider,
    FormControlLabel,
    MenuItem,
    Paper,
    Stack,
    TextField,
    Typography
} from '@mui/material'
import Inventory2RoundedIcon from '@mui/icons-material/Inventory2Rounded'
import SaveRoundedIcon from '@mui/icons-material/SaveRounded'
import StopRoundedIcon from '@mui/icons-material/StopRounded'
import VerifiedUserRoundedIcon from '@mui/icons-material/VerifiedUserRounded'

const PLUGIN_KEY = 'equipment_seller'
const SETTINGS_KEY = 'equipmentSellerConfig'
const DEFAULT_CONFIG = Object.freeze({
    rarities: {
        common: true,
        commonHero: true,
        rare: true,
        rareHero: true,
        epic: true,
        epicHero: true,
        legendary: false,
        legendaryHero: false
    },
    relics: {
        sellRelicsWithEffectsBelow: false,
        relicMaxEffects: '4',
        sellHeroRelicsWithEffectsBelow: false,
        heroRelicMaxEffects: '6'
    },
    safety: {
        skipEnchanted: true,
        skipGemmed: true
    },
    gems: {
        sellNormalGemsWithLevelAtMost: false,
        normalGemMaxLevel: '2',
        sellRelicGemsWithEffectsBelow: false,
        relicGemMaxEffects: '4'
    },
    intervalSeconds: '600',
    equipmentPerRun: '80',
    gemsPerRun: '200'
})

const CONFIG_LIMITS = Object.freeze({
    relicMaxEffects: { min: 0, max: 4 },
    heroRelicMaxEffects: { min: 0, max: 6 },
    normalGemMaxLevel: { min: 0, max: 9 },
    relicGemMaxEffects: { min: 0, max: 4 },
    intervalSeconds: { min: 30 },
    equipmentPerRun: { min: 1 },
    gemsPerRun: { min: 1 }
})

const RARITY_OPTIONS = Object.freeze([
    { key: 'common', label: 'Common' },
    { key: 'rare', label: 'Rare' },
    { key: 'epic', label: 'Epic' },
    { key: 'legendary', label: 'Legendary' },
    { key: 'commonHero', label: 'Common Hero' },
    { key: 'rareHero', label: 'Rare Hero' },
    { key: 'epicHero', label: 'Epic Hero' },
    { key: 'legendaryHero', label: 'Legendary Hero' }
])

const cloneValue = value =>
    typeof structuredClone === 'function' ?
        structuredClone(value) :
        JSON.parse(JSON.stringify(value ?? {}))

const normalizeNumberText = (value, fallback, minimum = 0) => {
    if ([undefined, null, ''].includes(value))
        return String(fallback)

    const parsed = Number.parseInt(String(value), 10)
    if (!Number.isFinite(parsed))
        return String(fallback)

    return String(Math.max(parsed, minimum))
}

const normalizeBoundedNumberText = (value, fallback, { min = 0, max } = {}) => {
    if ([undefined, null, ''].includes(value))
        return String(fallback)

    const parsed = Number.parseInt(String(value), 10)
    if (!Number.isFinite(parsed))
        return String(fallback)

    const minClamped = Math.max(parsed, min)
    return String(Number.isFinite(Number(max)) ? Math.min(minClamped, max) : minClamped)
}

const normalizeConfig = value => {
    const source = value && typeof value === 'object' ? value : {}
    const config = cloneValue(DEFAULT_CONFIG)

    Object.keys(config.rarities).forEach(key => {
        if (typeof source.rarities?.[key] === 'boolean')
            config.rarities[key] = source.rarities[key]
    })

    config.relics.sellRelicsWithEffectsBelow = Boolean(source.relics?.sellRelicsWithEffectsBelow)
    config.relics.relicMaxEffects = normalizeBoundedNumberText(source.relics?.relicMaxEffects, DEFAULT_CONFIG.relics.relicMaxEffects, CONFIG_LIMITS.relicMaxEffects)
    config.relics.sellHeroRelicsWithEffectsBelow = Boolean(source.relics?.sellHeroRelicsWithEffectsBelow)
    config.relics.heroRelicMaxEffects = normalizeBoundedNumberText(source.relics?.heroRelicMaxEffects, DEFAULT_CONFIG.relics.heroRelicMaxEffects, CONFIG_LIMITS.heroRelicMaxEffects)
    config.safety.skipEnchanted = source.safety?.skipEnchanted !== false
    config.safety.skipGemmed = source.safety?.skipGemmed !== false
    config.gems.sellNormalGemsWithLevelAtMost = Boolean(source.gems?.sellNormalGemsWithLevelAtMost)
    config.gems.normalGemMaxLevel = normalizeBoundedNumberText(source.gems?.normalGemMaxLevel, DEFAULT_CONFIG.gems.normalGemMaxLevel, CONFIG_LIMITS.normalGemMaxLevel)
    config.gems.sellRelicGemsWithEffectsBelow = false
    config.gems.relicGemMaxEffects = normalizeBoundedNumberText(source.gems?.relicGemMaxEffects, DEFAULT_CONFIG.gems.relicGemMaxEffects, CONFIG_LIMITS.relicGemMaxEffects)
    config.intervalSeconds = normalizeNumberText(source.intervalSeconds, DEFAULT_CONFIG.intervalSeconds, CONFIG_LIMITS.intervalSeconds.min)
    config.equipmentPerRun = normalizeNumberText(source.equipmentPerRun, DEFAULT_CONFIG.equipmentPerRun, CONFIG_LIMITS.equipmentPerRun.min)
    config.gemsPerRun = normalizeNumberText(source.gemsPerRun, DEFAULT_CONFIG.gemsPerRun, CONFIG_LIMITS.gemsPerRun.min)

    return config
}

export const buildEquipmentSellerPluginState = (userPlugins, nextConfig, nextState = true) => {
    const nextPlugins = cloneValue(userPlugins ?? {})

    nextPlugins[PLUGIN_KEY] ??= {}
    nextPlugins[PLUGIN_KEY].state = Boolean(nextState)
    nextPlugins[PLUGIN_KEY][SETTINGS_KEY] = normalizeConfig(nextConfig)

    return nextPlugins
}

const FIELD_INPUT_SX = {
    width: { xs: '100%', sm: 118 },
    '& .MuiOutlinedInput-root': {
        minHeight: 36,
        borderRadius: '8px',
        backgroundColor: 'rgba(12, 23, 40, 0.88)',
        color: '#f5f7ff',
        '& fieldset': {
            borderColor: 'rgba(129, 146, 178, 0.26)'
        },
        '&:hover fieldset': {
            borderColor: 'rgba(143, 180, 255, 0.44)'
        },
        '&.Mui-focused fieldset': {
            borderColor: '#8fb4ff'
        }
    },
    '& .MuiInputBase-input': {
        py: 0.95,
        color: '#f5f7ff',
        fontSize: '0.94rem',
        fontWeight: 700
    },
    '& .MuiInputBase-input.Mui-disabled': {
        WebkitTextFillColor: 'rgba(245, 247, 255, 0.52)'
    }
}

const CHECKBOX_SX = {
    p: 0,
    mr: 1.1,
    color: 'rgba(143, 180, 255, 0.48)',
    '&.Mui-checked': {
        color: '#8fb4ff'
    }
}

const GROUP_SX = {
    px: { xs: 1.4, md: 1.65 },
    py: 1.45,
    borderRadius: '8px',
    border: '1px solid rgba(129, 146, 178, 0.16)',
    backgroundColor: 'rgba(16, 27, 46, 0.42)'
}

function Helper({ children, color = '#9fb0cc' }) {
    if (!children)
        return null

    return (
        <Typography sx={{ mt: 0.45, color, fontSize: '0.84rem', lineHeight: 1.35 }}>
            {children}
        </Typography>
    )
}

function SectionTitle({ children, helper }) {
    return (
        <Box sx={{ mb: 0.65 }}>
            <Typography sx={{ color: '#f5f7ff', fontSize: '0.93rem', fontWeight: 800 }}>
                {children}
            </Typography>
            <Helper>{helper}</Helper>
        </Box>
    )
}

function GoldCheckbox({ checked, onChange, label, disabled = false }) {
    return (
        <FormControlLabel
            control={<Checkbox checked={checked} disabled={disabled} onChange={event => onChange(event.target.checked)} sx={CHECKBOX_SX} />}
            label={label}
            sx={{
                m: 0,
                minHeight: 28,
                color: disabled ? 'rgba(245, 247, 255, 0.44)' : '#f5f7ff',
                '& .MuiFormControlLabel-label': {
                    fontSize: '0.96rem',
                    fontWeight: 600
                }
            }}
        />
    )
}

function NumberField({ value, onChange, width = 118, disabled = false, min, max }) {
    return (
        <TextField
            value={value}
            disabled={disabled}
            onChange={event => onChange(event.target.value)}
            inputProps={{ inputMode: 'numeric', pattern: '[0-9]*', min, max }}
            sx={{ ...FIELD_INPUT_SX, width: { xs: '100%', sm: width } }}
        />
    )
}

function LimitRow({ checked, onCheckedChange, label, value, onValueChange, suffix, helper, disabled = false, min, max }) {
    return (
        <Box
            sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'minmax(0, 1fr) 170px 68px' },
                alignItems: 'center',
                columnGap: 1.4,
                rowGap: 0.8,
                minHeight: 48
            }}
        >
            <Box>
                <GoldCheckbox checked={checked} onChange={onCheckedChange} label={label} disabled={disabled} />
                <Helper color={disabled ? 'rgba(159, 176, 204, 0.58)' : '#9fb0cc'}>{helper}</Helper>
            </Box>
            <NumberField value={value} onChange={onValueChange} width={96} disabled={disabled || !checked} min={min} max={max} />
            <Typography sx={{ color: '#9fb0cc', fontSize: '0.86rem', whiteSpace: 'nowrap' }}>
                {suffix}
            </Typography>
        </Box>
    )
}

const formatEnabledCount = values =>
    Object.values(values ?? {}).filter(Boolean).length

const buildRunSummary = config => {
    const rarityCount = formatEnabledCount(config.rarities)
    const parts = [
        `${rarityCount}/8 rarities`,
        config.relics.sellRelicsWithEffectsBelow ? `Relics <= ${config.relics.relicMaxEffects} effects` : 'Relics off',
        config.relics.sellHeroRelicsWithEffectsBelow ? `Hero relics <= ${config.relics.heroRelicMaxEffects} effects` : 'Hero relics off',
        config.gems.sellNormalGemsWithLevelAtMost ? `Normal gems <= level ${config.gems.normalGemMaxLevel}` : 'Normal gems off'
    ]

    return parts.join(' | ')
}

const buildDecisionSummary = config => {
    const selectedRarities = RARITY_OPTIONS
        .filter(option => config.rarities[option.key])
        .map(option => option.label)

    const willSell = [
        selectedRarities.length > 0 ?
            `Non-relic equipment: ${selectedRarities.join(', ')}` :
            'Non-relic equipment: no rarities selected',
        config.relics.sellRelicsWithEffectsBelow ?
            `Relic equipment: ${config.relics.relicMaxEffects} effects or fewer` :
            'Relic equipment: off',
        config.relics.sellHeroRelicsWithEffectsBelow ?
            `Relic heroes: ${config.relics.heroRelicMaxEffects} effects or fewer` :
            'Relic heroes: off',
        config.gems.sellNormalGemsWithLevelAtMost ?
            `Normal gems: level ${config.gems.normalGemMaxLevel} or lower` :
            'Normal gems: off'
    ]

    const protectedItems = [
        'Rift / outer event equipment and gems',
        'Equipment not used by commander or castellan inventories',
        config.safety.skipEnchanted ? 'Enchanted equipment' : 'Enchanted equipment is not protected',
        config.safety.skipGemmed ? 'Equipment with attached gems' : 'Gemmed equipment is not protected'
    ]

    return {
        willSell,
        protectedItems,
        unavailable: [
            'Relic gem selling is disabled until its inventory data is parsed safely'
        ]
    }
}

function SummaryList({ title, items, tone = 'default' }) {
    const color = tone === 'warning' ? '#f8e7b2' : tone === 'success' ? '#b9d2ff' : '#f5f7ff'
    const markerColor = tone === 'warning' ? '#f6c455' : tone === 'success' ? '#8fb4ff' : '#9fb0cc'

    return (
        <Box sx={{ ...GROUP_SX, py: 1.25 }}>
            <Typography sx={{ color, fontSize: '0.92rem', fontWeight: 850, mb: 0.8 }}>
                {title}
            </Typography>
            <Stack spacing={0.55}>
                {items.map(item => (
                    <Stack key={item} direction="row" spacing={0.8} alignItems="flex-start">
                        <Box sx={{ width: 6, height: 6, mt: '7px', borderRadius: '50%', backgroundColor: markerColor, flexShrink: 0 }} />
                        <Typography sx={{ color: '#9fb0cc', fontSize: '0.84rem', lineHeight: 1.35 }}>
                            {item}
                        </Typography>
                    </Stack>
                ))}
            </Stack>
        </Box>
    )
}

export default function EquipmentSellerSettingsPanel({ userPlugins, onChange, onSave, selectedUser }) {
    const pluginState = React.useMemo(
        () => userPlugins?.[PLUGIN_KEY] && typeof userPlugins[PLUGIN_KEY] === 'object' ? userPlugins[PLUGIN_KEY] : {},
        [userPlugins]
    )
    const savedConfig = pluginState?.[SETTINGS_KEY]
    const [draft, setDraft] = React.useState(() => normalizeConfig(savedConfig))
    const accountLabel = String(selectedUser?.name ?? selectedUser?.username ?? 'Current account')
    const pluginEnabled = pluginState?.state === true
    const runSummary = React.useMemo(() => buildRunSummary(draft), [draft])
    const decisionSummary = React.useMemo(() => buildDecisionSummary(draft), [draft])

    React.useEffect(() => {
        setDraft(normalizeConfig(savedConfig))
    }, [savedConfig])

    const update = updater => {
        setDraft(previous => {
            const next = cloneValue(previous)
            updater(next)
            return next
        })
    }

    const handleSave = () => {
        const nextPlugins = buildEquipmentSellerPluginState(userPlugins, draft, true)
        if (onSave)
            onSave(nextPlugins)
        else
            onChange(nextPlugins)
    }

    const handleStop = () => {
        const nextPlugins = buildEquipmentSellerPluginState(userPlugins, draft, false)
        if (onSave)
            onSave(nextPlugins)
        else
            onChange(nextPlugins)
    }

    return (
        <Paper
            sx={{
                p: { xs: 2.2, md: 3.1 },
                borderRadius: '8px',
                border: '1px solid rgba(129, 146, 178, 0.18)',
                background: 'linear-gradient(180deg, rgba(27, 40, 63, 0.9) 0%, rgba(24, 35, 56, 0.86) 100%)',
                boxShadow: '0 22px 46px rgba(7, 13, 26, 0.22)'
            }}
        >
            <Stack spacing={2.55}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: 0.6 }}>
                    <Stack direction="row" spacing={1.15} alignItems="center">
                        <Box
                            sx={{
                                width: 31,
                                height: 31,
                                borderRadius: '8px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                border: '1px solid rgba(143, 180, 255, 0.36)',
                                backgroundColor: 'rgba(143, 180, 255, 0.14)'
                            }}
                        >
                            <Inventory2RoundedIcon sx={{ color: '#8fb4ff', fontSize: 21 }} />
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '1.42rem', fontWeight: 800 }}>
                                Equipment Seller Settings
                            </Typography>
                            <Typography sx={{ color: '#9fb0cc', fontSize: '0.98rem', lineHeight: 1.35 }}>
                                {runSummary}
                            </Typography>
                        </Box>
                    </Stack>
                    <Chip
                        icon={<VerifiedUserRoundedIcon />}
                        label={pluginEnabled ? 'Active' : 'Stopped'}
                        sx={{
                            height: 28,
                            borderRadius: '8px',
                            color: pluginEnabled ? '#07142a' : '#f5f7ff',
                            backgroundColor: pluginEnabled ? '#8fb4ff' : 'rgba(255,255,255,0.08)',
                            border: '1px solid rgba(143, 180, 255, 0.32)',
                            fontWeight: 850,
                            '& .MuiChip-icon': {
                                color: pluginEnabled ? '#07142a' : '#9fb0cc'
                            }
                        }}
                    />
                </Stack>

                <Alert
                    severity="warning"
                    sx={{
                        py: 0.85,
                        borderRadius: '8px',
                        border: '1px solid rgba(245, 194, 66, 0.28)',
                        backgroundColor: 'rgba(105, 78, 18, 0.22)',
                        color: '#f8e7b2',
                        '& .MuiAlert-icon': {
                            color: '#f6c455'
                        }
                    }}
                >
                    Rules below sell inventory items permanently. Keep both safety filters enabled unless you deliberately want to sell upgraded or gemmed equipment.
                </Alert>

                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '180px minmax(0, 1fr)' }, gap: 1.8 }}>
                    <Box>
                        <SectionTitle>Account</SectionTitle>
                        <TextField
                            select
                            value={accountLabel}
                            disabled
                            fullWidth
                            sx={FIELD_INPUT_SX}
                        >
                            <MenuItem value={accountLabel}>{accountLabel}</MenuItem>
                        </TextField>
                    </Box>
                    <Box sx={{ ...GROUP_SX, py: 1.25 }}>
                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.4} divider={<Divider orientation="vertical" flexItem sx={{ borderColor: 'rgba(129, 146, 178, 0.16)' }} />}>
                            <Box sx={{ minWidth: 150 }}>
                                <Typography sx={{ color: '#f5f7ff', fontWeight: 850, fontSize: '0.9rem' }}>Cycle</Typography>
                                <Typography sx={{ color: '#9fb0cc', fontSize: '0.86rem' }}>{draft.intervalSeconds}s between scans</Typography>
                            </Box>
                            <Box sx={{ minWidth: 150 }}>
                                <Typography sx={{ color: '#f5f7ff', fontWeight: 850, fontSize: '0.9rem' }}>Equipment limit</Typography>
                                <Typography sx={{ color: '#9fb0cc', fontSize: '0.86rem' }}>{draft.equipmentPerRun} items per scan</Typography>
                            </Box>
                            <Box sx={{ minWidth: 150 }}>
                                <Typography sx={{ color: '#f5f7ff', fontWeight: 850, fontSize: '0.9rem' }}>Gem limit</Typography>
                                <Typography sx={{ color: '#9fb0cc', fontSize: '0.86rem' }}>{draft.gemsPerRun} gems per scan</Typography>
                            </Box>
                        </Stack>
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: 'grid',
                        gridTemplateColumns: { xs: '1fr', xl: '1.2fr 1fr 1fr' },
                        gap: 1.5
                    }}
                >
                    <SummaryList title="What will be sold" items={decisionSummary.willSell} tone="success" />
                    <SummaryList title="Protected automatically" items={decisionSummary.protectedItems} />
                    <SummaryList title="Not sold by this module" items={decisionSummary.unavailable} tone="warning" />
                </Box>

                <Box>
                    <SectionTitle helper="Applies to non-relic equipment only. Relic equipment uses the relic rules below.">Sell by Rarity</SectionTitle>
                    <Box
                        sx={{
                            ...GROUP_SX,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, minmax(0, 1fr))', lg: 'repeat(4, minmax(0, 1fr))' },
                            columnGap: { xs: 1.5, md: 4 },
                            rowGap: 0.9
                        }}
                    >
                        {RARITY_OPTIONS.map(option => (
                            <GoldCheckbox
                                key={option.key}
                                checked={draft.rarities[option.key]}
                                onChange={value => update(next => { next.rarities[option.key] = value })}
                                label={option.label}
                            />
                        ))}
                    </Box>
                </Box>

                <Box>
                    <SectionTitle helper="The seller counts effects and sells only relic items at or below the configured effect count.">Relics</SectionTitle>
                    <Box sx={GROUP_SX}>
                        <LimitRow
                            checked={draft.relics.sellRelicsWithEffectsBelow}
                            onCheckedChange={value => update(next => { next.relics.sellRelicsWithEffectsBelow = value })}
                            label="Sell relic equipment with effects <="
                            value={draft.relics.relicMaxEffects}
                            onValueChange={value => update(next => { next.relics.relicMaxEffects = value })}
                            suffix="(max 4)"
                            min={CONFIG_LIMITS.relicMaxEffects.min}
                            max={CONFIG_LIMITS.relicMaxEffects.max}
                            helper="Commander and castellan relic equipment, not heroes."
                        />
                        <LimitRow
                            checked={draft.relics.sellHeroRelicsWithEffectsBelow}
                            onCheckedChange={value => update(next => { next.relics.sellHeroRelicsWithEffectsBelow = value })}
                            label="Sell relic hero equipment with effects <="
                            value={draft.relics.heroRelicMaxEffects}
                            onValueChange={value => update(next => { next.relics.heroRelicMaxEffects = value })}
                            suffix="(max 6)"
                            min={CONFIG_LIMITS.heroRelicMaxEffects.min}
                            max={CONFIG_LIMITS.heroRelicMaxEffects.max}
                            helper="Hero relics are handled separately from normal relic equipment."
                        />
                    </Box>
                </Box>

                <Box>
                    <SectionTitle helper="These filters prevent selling items that are more likely to be intentionally kept.">Safety Filters</SectionTitle>
                    <Box sx={GROUP_SX}>
                        <Stack spacing={1}>
                            <GoldCheckbox
                                checked={draft.safety.skipEnchanted}
                                onChange={value => update(next => { next.safety.skipEnchanted = value })}
                                label="Skip enchanted equipment"
                            />
                            <Helper>Does not sell equipment with enchantment level above 0.</Helper>
                            <GoldCheckbox
                                checked={draft.safety.skipGemmed}
                                onChange={value => update(next => { next.safety.skipGemmed = value })}
                                label="Skip gemmed equipment"
                            />
                            <Helper>Does not sell equipment that still has a gem attached.</Helper>
                        </Stack>
                    </Box>
                </Box>

                <Box>
                    <SectionTitle helper="Normal gems can be sold by level. Relic gem selling stays disabled until the inventory payload is parsed safely.">Gems</SectionTitle>
                    <Box sx={GROUP_SX}>
                        <LimitRow
                            checked={draft.gems.sellNormalGemsWithLevelAtMost}
                            onCheckedChange={value => update(next => { next.gems.sellNormalGemsWithLevelAtMost = value })}
                            label="Sell normal gems with level <="
                            value={draft.gems.normalGemMaxLevel}
                            onValueChange={value => update(next => { next.gems.normalGemMaxLevel = value })}
                            suffix="(0-9)"
                            min={CONFIG_LIMITS.normalGemMaxLevel.min}
                            max={CONFIG_LIMITS.normalGemMaxLevel.max}
                            helper="Only normal gems; rift/event gems are skipped by backend safety checks."
                        />
                        <LimitRow
                            checked={false}
                            onCheckedChange={() => {}}
                            label="Sell relic gems with effects <="
                            value={draft.gems.relicGemMaxEffects}
                            onValueChange={value => update(next => { next.gems.relicGemMaxEffects = value })}
                            suffix="(max 4)"
                            disabled
                            min={CONFIG_LIMITS.relicGemMaxEffects.min}
                            max={CONFIG_LIMITS.relicGemMaxEffects.max}
                            helper="Disabled: backend scans relic gems but does not sell them yet."
                        />
                    </Box>
                </Box>

                <Box>
                    <SectionTitle helper="These limits control pacing so the seller does not process the entire inventory in one scan.">Run Limits</SectionTitle>
                    <Box
                        sx={{
                            ...GROUP_SX,
                            display: 'grid',
                            gridTemplateColumns: { xs: '1fr', md: 'repeat(3, minmax(0, 1fr))' },
                            gap: 1.6
                        }}
                    >
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '0.92rem', fontWeight: 800, mb: 0.7 }}>
                                Cycle interval
                            </Typography>
                            <NumberField
                                value={draft.intervalSeconds}
                                onChange={value => update(next => { next.intervalSeconds = value })}
                                width={128}
                                min={CONFIG_LIMITS.intervalSeconds.min}
                            />
                            <Helper>Seconds between scans. Minimum saved value is 30.</Helper>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '0.92rem', fontWeight: 800, mb: 0.7 }}>
                                Equipment per scan
                            </Typography>
                            <NumberField
                                value={draft.equipmentPerRun}
                                onChange={value => update(next => { next.equipmentPerRun = value })}
                                width={128}
                                min={CONFIG_LIMITS.equipmentPerRun.min}
                            />
                            <Helper>Maximum equipment items sold in one run.</Helper>
                        </Box>
                        <Box>
                            <Typography sx={{ color: '#f5f7ff', fontSize: '0.92rem', fontWeight: 800, mb: 0.7 }}>
                                Normal gems per scan
                            </Typography>
                            <NumberField
                                value={draft.gemsPerRun}
                                onChange={value => update(next => { next.gemsPerRun = value })}
                                width={128}
                                min={CONFIG_LIMITS.gemsPerRun.min}
                            />
                            <Helper>Maximum normal gems sold in one run.</Helper>
                        </Box>
                    </Box>
                </Box>

                <Stack direction="row" spacing={1.1} alignItems="center">
                    <Button
                        variant="outlined"
                        startIcon={<SaveRoundedIcon />}
                        onClick={handleSave}
                        sx={{
                            minHeight: 36,
                            borderRadius: '8px',
                            borderColor: 'rgba(143, 180, 255, 0.42)',
                            color: '#f5f7ff',
                            textTransform: 'none',
                            fontWeight: 800,
                            '&:hover': {
                                borderColor: 'rgba(143, 180, 255, 0.68)',
                                backgroundColor: 'rgba(143, 180, 255, 0.08)'
                            }
                        }}
                    >
                        Save & Start
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<StopRoundedIcon />}
                        onClick={handleStop}
                        sx={{
                            minHeight: 36,
                            borderRadius: '8px',
                            backgroundColor: '#d9483f',
                            color: '#fff',
                            textTransform: 'none',
                            fontWeight: 800,
                            boxShadow: 'none',
                            '&:hover': {
                                backgroundColor: '#c93e36',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Save & Stop
                    </Button>
                </Stack>
            </Stack>
        </Paper>
    )
}
